import React, { Fragment, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Col, Row } from "reactstrap";
import { withTranslation } from "react-i18next";
import get from "lodash/get";
import PropTypes from "prop-types";

import Collapse from "./Collapse";
import TariffField from "Components/TariffField";

import { getOptionLabel } from "common/enums";
import { CONSUMER, applicationTypes } from "common/constant";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { useLazyUtilityConsumerQuery } from "apis/UtilityConsumer";

const ConnectionLoadView = ({ applicationData, isExpandable = true, t }) => {
  const { roles } = useSelector((state) => state.Login);
  const [getConsumerDetails, { data: { data: consumerDetails } = {} }] =
    useLazyUtilityConsumerQuery();

  const {
    applicationId,
    applicationType: { id: applicationTypeId } = {},
    applicationStatus: { name: applicationStatusName } = {},
    connectionLoad: {
      powerRequirementUnit,
      supplyVoltageUnit,
      increaceOrDecreaseOfLoadUnit,
      totalPowerRequirementUnit,
    },
    utilityConsumerId,
    approvedBy,
    isGreenPower,
    isSolar,
  } = applicationData;

  const fields = useMemo(
    () => [
      {
        name: "Backup Power Alteration",
        key: "connectionLoad.backupPowerAlteration",
        renderField: (value) => getOptionLabel("powerAlterationOptions", value),
        isRequired: false,
        isVisible: applicationTypeId === applicationTypes["Backup Power"],
      },
      {
        name: "Existing Backup",
        key: "utilityConsumerId",
        renderField: () => {
          const backupkVA = consumerDetails?.connectionLoad?.backupkVA ?? "";
          try {
            return backupkVA ? `${backupkVA} kVA` : "N/A";
          } catch (error) {
            console.error(error);
          }
          return "-";
        },
        isVisible: applicationTypeId === applicationTypes["Backup Power"],
        isRequired: true,
      },
      {
        name: "Total Backup Power",
        key: "connectionLoad.backupkVA",
        renderField: (value) => (value ? `${value} kVA` : "N/A"),
        isRequired: false,
      },
      {
        name: "Tariff",
        key: "tariff",
        isVisible: !roles.includes(CONSUMER),
        renderField: (value) => (
          <TariffField
            applicationId={applicationId}
            applicationTypeId={applicationTypeId}
            applicationStatusName={applicationStatusName}
            tariff={value ?? {}}
            approvedBy={approvedBy}
            isGreenPower={isGreenPower}
            isSolar={isSolar}
          />
        ),
        isRequired: true,
      },
      {
        name: "Load Details",
        key: "connectionLoad.loadDetail",
        isRequired: true,
        renderField: (value) => getOptionLabel("loadOptions", value) || "-",
      },
      {
        name: "Existing Contract Demand",
        key: "connectionLoad.powerRequirement",
        isRequired: true,
        renderField: (value) =>
          value
            ? `${value} ${getOptionLabel("unitOptions", powerRequirementUnit)}`
            : "-",
        isVisible: [
          applicationTypes["Demand Extension"],
          applicationTypes["Demand Reduction"],
          applicationTypes["Premise Removal"],
          applicationTypes["Premise Amalgamation"],
        ].includes(applicationTypeId),
      },
      {
        name: "Time Shift",
        key: "connectionLoad.timeShiftEnum",
        isRequired: false,
        renderField: (value) =>
          value ? getOptionLabel("timeshiftingOptions", value) : "N/A",
      },
      {
        name: "Increase of Load",
        key: "connectionLoad.increaseOfLoad",
        isRequired: true,
        renderField: (value) =>
          value
            ? `${value} ${getOptionLabel(
                "unitOptions",
                increaceOrDecreaseOfLoadUnit
              )}`
            : applicationTypeId === applicationTypes["Premise Amalgamation"]
            ? "N/A"
            : "-",
        isVisible: [
          applicationTypes["Demand Extension"],
          applicationTypes["Premise Amalgamation"],
        ].includes(applicationTypeId),
      },
      {
        name: "Decrease of Load",
        key: "connectionLoad.decreaseOfLoad",
        isRequired: true,
        renderField: (value) =>
          value
            ? `${value} ${getOptionLabel(
                "unitOptions",
                increaceOrDecreaseOfLoadUnit
              )}`
            : applicationTypeId === applicationTypes["Premise Removal"]
            ? "N/A"
            : "-",
        isVisible: [
          applicationTypes["Demand Reduction"],
          applicationTypes["Premise Removal"],
        ].includes(applicationTypeId),
      },
      {
        name: "Supply Voltage",
        key: "connectionLoad.supplyVoltage",
        isRequired: true,
        renderField: (value) =>
          value
            ? `${value} ${getOptionLabel("unitOptions", supplyVoltageUnit)}`
            : "-",
      },
      {
        name: "Total Power Requirement",
        key: "connectionLoad.totalPowerRequirement",
        isRequired: true,
        renderField: (value) =>
          value
            ? `${value} ${getOptionLabel(
                "unitOptions",
                totalPowerRequirementUnit
              )}`
            : "-",
      },
      {
        name: "Green Power",
        key: "connectionLoad.greenPower",
        isRequired: false,
        renderField: (value) => (value ? `${value} %` : "No"),
      },
      {
        name: "Connection Start Date",
        key: "connectionLoad.expectedConnectionStartDate",
        isVisible:
          applicationTypeId === applicationTypes["Temporary Connection"],
        isRequired: true,
        renderField: (value) =>
          value ? convertUTCDateToLocalDate(value).format("DD-MM-YYYY") : "-",
      },
      {
        name: "Connection End Date",
        key: "connectionLoad.expectedConnectionEndDate",
        isVisible:
          applicationTypeId === applicationTypes["Temporary Connection"],
        isRequired: true,
        renderField: (value) =>
          value ? convertUTCDateToLocalDate(value).format("DD-MM-YYYY") : "-",
      },
    ],
    [consumerDetails, applicationTypeId]
  );

  useEffect(() => {
    if (utilityConsumerId) {
      (async () => {
        try {
          await getConsumerDetails(utilityConsumerId);
        } catch (error) {
          console.error(error);
        }
      })();
    }
  }, [utilityConsumerId]);

  return (
    <Collapse title="Connection Load" isExpandable={isExpandable}>
      <Row className="d-flex  mb-2 ">
        {fields.map(({ isVisible, name, key, isRequired, renderField }) => {
          const value = get(applicationData, key, "");
          if (typeof isVisible !== "boolean" || isVisible) {
            return (
              <Col key={key} lg={6} className="mb-1">
                <Row>
                  <Col md={5}>{t(name)}:</Col>
                  <Col
                    md={7}
                    className="fw-semibold"
                    style={{ wordBreak: "break-word" }}
                  >
                    {renderField
                      ? renderField(value)
                      : get(applicationData, key, null) ??
                        (isRequired ? "-" : "N/A")}
                  </Col>
                </Row>
              </Col>
            );
          }
          return <Fragment key={key} />;
        })}
      </Row>
    </Collapse>
  );
};

ConnectionLoadView.propTypes = {
  applicationData: PropTypes.object,
  isExpandable: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(ConnectionLoadView);
